import type { ReactNode } from 'react';
import {
  Alert,
  AttachmentGallery,
  Badge,
  Button,
  CopyButton,
  DetailSection,
  Fact,
  Facts,
  Icon,
  NotProvided,
  buttonClasses,
  type IconName,
} from '@evogard/ui';
import type { ClaimFile, Peripheral, PeripheralForm } from '@evogard/contract';
import {
  PAYMENT_LINK_STATE_LABELS,
  PERIPHERAL_REQUEST_ORIGINS,
  format,
  formatInstant,
  formatPhone,
  fromColumn,
} from '@evogard/domain';
import { SettlementFacts } from '../../../components/ChargeSettlement.js';
import {
  peripheralFileUrl,
  peripheralReceiptUrl,
  type usePeripheralMutations,
} from './usePeripherals.js';

const money = (value: string | null) => {
  const m = fromColumn(value);
  return m === null ? null : format(m);
};

/**
 * A aba Detalhes da mesa de Periféricos (20/10/2026): o que veio com o
 * acionamento, num lugar só — quem pediu, o veículo, onde o serviço será
 * feito, o orçamento do associado e o pagamento da PI.
 *
 * Até aqui o Local do serviço ficava embaixo dos itens e o pagamento no alto
 * do miolo, acima das abas; a mesa do sinistro não tem nenhum dos dois, e o
 * resto da mesa continua igual à dela. As seções são as da ficha do sinistro
 * (`DetailSection`), com o ícone de cada assunto, e cada rótulo leva o ícone
 * do dado.
 */
export function PeripheralDetails({
  peripheral: p,
  form,
  files,
  canWork,
  regenerate,
}: {
  peripheral: Peripheral;
  /** O formulário da etapa atual — de onde saem os contatos do cadastro. */
  form: PeripheralForm | undefined;
  files: ClaimFile[] | undefined;
  canWork: boolean;
  regenerate: ReturnType<typeof usePeripheralMutations>['regeneratePaymentLink'];
}) {
  const budget = (files ?? []).filter((f) => f.kind === 'orcamento');
  return (
    <div className="flex flex-col gap-4">
      <RequestSection peripheral={p} />
      <MemberSection peripheral={p} form={form} />
      <VehicleSection peripheral={p} />
      <RepairPlaceSection peripheral={p} />
      {budget.length > 0 && <BudgetSection peripheralId={p.id} files={budget} />}
      <PaymentSection peripheral={p} canWork={canWork} regenerate={regenerate} />
    </div>
  );
}

/** O rótulo de um dado com o ícone dele na frente. */
function Labeled({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon nome={icon} size={16} className="text-texto-tenue" />
      {children}
    </span>
  );
}

const orNot = (value: string | null | undefined): ReactNode =>
  value ? value : <NotProvided />;

function RequestSection({ peripheral: p }: { peripheral: Peripheral }) {
  const origin = PERIPHERAL_REQUEST_ORIGINS.find((o) => o.value === p.requestOrigin);
  return (
    <DetailSection icon="demandas" title="O acionamento">
      <Facts>
        <Fact label={<Labeled icon="calendario">Aberto em</Labeled>}>
          <span className="tabular-nums">{formatInstant(p.createdAt)}</span>
        </Fact>
        <Fact label={<Labeled icon="bandeira">Origem</Labeled>}>
          {origin ? origin.label : orNot(p.requestOrigin)}
        </Fact>
        <Fact label={<Labeled icon="telefone">WhatsApp do pedido</Labeled>}>
          {p.requestWhatsapp ? (
            <span className="tabular-nums">{formatPhone(p.requestWhatsapp)}</span>
          ) : (
            <NotProvided />
          )}
        </Fact>
        <Fact wide label={<Labeled icon="documento">O que aconteceu</Labeled>}>
          {p.occurrenceDescription ? (
            <span className="font-normal whitespace-pre-line">
              {p.occurrenceDescription}
            </span>
          ) : (
            <NotProvided />
          )}
        </Fact>
      </Facts>
    </DetailSection>
  );
}

/**
 * O dono do veículo. Os telefones e e-mails são os que a tentativa de contato
 * oferece (`contactSuggestions`): o WhatsApp do pedido primeiro, depois o
 * cadastro.
 */
function MemberSection({
  peripheral: p,
  form,
}: {
  peripheral: Peripheral;
  form: PeripheralForm | undefined;
}) {
  const contacts = form?.contactSuggestions;
  return (
    <DetailSection icon="pessoa" title="O associado">
      <Facts>
        <Fact label={<Labeled icon="pessoa">Nome</Labeled>}>
          {orNot(p.vehicle?.memberName)}
        </Fact>
        <Fact label={<Labeled icon="telefone">Telefones</Labeled>}>
          {contacts && contacts.phones.length > 0 ? (
            <span className="flex flex-col tabular-nums">
              {contacts.phones.map((phone) => (
                <span key={phone}>{formatPhone(phone)}</span>
              ))}
            </span>
          ) : (
            <NotProvided />
          )}
        </Fact>
        <Fact label={<Labeled icon="email">E-mails</Labeled>}>
          {contacts && contacts.emails.length > 0 ? (
            <span className="flex flex-col">
              {contacts.emails.map((email) => (
                <span key={email}>{email}</span>
              ))}
            </span>
          ) : (
            <NotProvided />
          )}
        </Fact>
      </Facts>
    </DetailSection>
  );
}

function VehicleSection({ peripheral: p }: { peripheral: Peripheral }) {
  const v = p.vehicle;
  return (
    <DetailSection
      icon="veiculo"
      title="O veículo vinculado"
      descricao="Lido do cadastro da casa."
    >
      {v ? (
        <Facts>
          <Fact label={<Labeled icon="veiculo">Placa</Labeled>}>
            {v.plate ? (
              <span className="font-mono tabular-nums">{v.plate}</span>
            ) : (
              <NotProvided>sem placa</NotProvided>
            )}
          </Fact>
          <Fact label={<Labeled icon="etiqueta">Marca e modelo</Labeled>}>
            {orNot([v.brand, v.model].filter(Boolean).join(' ') || null)}
          </Fact>
          <Fact label={<Labeled icon="calendario">Ano do modelo</Labeled>}>
            {orNot(v.modelYear)}
          </Fact>
          <Fact label={<Labeled icon="codigo-barras">Código</Labeled>}>
            <span className="font-mono tabular-nums">{v.code}</span>
          </Fact>
          <Fact label={<Labeled icon="escudo">Plano</Labeled>}>{orNot(v.plan)}</Fact>
          <Fact label={<Labeled icon="informacao">Situação no SGA</Labeled>}>
            {orNot(v.status)}
          </Fact>
          <Fact label={<Labeled icon="percentual">Cobertura</Labeled>}>
            {p.coveragePercent === null ? <NotProvided /> : `${p.coveragePercent}%`}
          </Fact>
        </Facts>
      ) : (
        <p className="text-sm text-texto-suave">
          O veículo {p.vehicleCode} ainda não está no cadastro da casa.
        </p>
      )}
    </DetailSection>
  );
}

/**
 * Onde o serviço será feito (`repair_address_id`) — hoje, só o pedido do
 * portal o informa (19/10/2026). O que a mesa abriu não tem, e a seção diz.
 */
function RepairPlaceSection({ peripheral: p }: { peripheral: Peripheral }) {
  const a = p.repairAddress;
  return (
    <DetailSection
      icon="mapa"
      title="O local do serviço"
      actions={
        a?.locationLink && (
          <a
            href={a.locationLink}
            target="_blank"
            rel="noreferrer"
            className={buttonClasses({ variant: 'contorno', size: 'sm' })}
          >
            <Icon nome="mapa" size={16} />
            Abrir no mapa
          </a>
        )
      }
    >
      {a ? (
        <Facts>
          <Fact label={<Labeled icon="inicio">Endereço</Labeled>}>
            {orNot([a.street, a.number].filter(Boolean).join(', ') || null)}
            {a.complement && (
              <span className="block text-xs font-normal text-texto-suave">
                {a.complement}
              </span>
            )}
          </Fact>
          <Fact label={<Labeled icon="mapa">Bairro e cidade</Labeled>}>
            {orNot(
              [a.neighborhood, a.city && a.state ? `${a.city}/${a.state}` : a.city]
                .filter(Boolean)
                .join(' · ') || null,
            )}
          </Fact>
          <Fact label={<Labeled icon="codigo-barras">CEP</Labeled>}>
            {a.zipCode ? (
              <span className="tabular-nums">{formatZipCode(a.zipCode)}</span>
            ) : (
              <NotProvided />
            )}
          </Fact>
          {a.referencePoint && (
            <Fact wide label={<Labeled icon="bandeira">Ponto de referência</Labeled>}>
              {a.referencePoint}
            </Fact>
          )}
        </Facts>
      ) : (
        <p className="text-sm text-texto-suave">Não informado na abertura.</p>
      )}
    </DetailSection>
  );
}

/** `60125100` → `60125-100`; o que não tem oito dígitos volta como veio. */
function formatZipCode(zip: string) {
  const d = zip.replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : zip;
}

/**
 * O orçamento que o associado mandou no pedido do portal (`kind` `orcamento`).
 * É o mesmo arquivo que está em Anexos; aqui ele aparece junto do resto do
 * pedido. Sem orçamento, a seção não aparece — ele é opcional no portal.
 */
function BudgetSection({
  peripheralId,
  files,
}: {
  peripheralId: number;
  files: ClaimFile[];
}) {
  return (
    <DetailSection icon="documento" title="O orçamento do associado">
      <AttachmentGallery
        label="Orçamento"
        items={files.map((d) => ({
          key: String(d.id),
          nome: d.fileName,
          typeMime: d.mimeType,
          sizeBytes: d.sizeBytes,
          detail: formatInstant(d.createdAt),
          resolveUrl: () => peripheralFileUrl(peripheralId, d.id),
        }))}
      />
    </DetailSection>
  );
}

/**
 * O pagamento da PI pelo link (19/10/2026), que até 20/10/2026 ficava no alto
 * do miolo.
 *
 * Diz o que o associado já fez: se o link ainda vale, se ele escolheu Pix ou
 * cartão, se pagou. "Gerar link novo" arquiva o pedido anterior, cancela a
 * cobrança aberta dele no Asaas e manda a mensagem da PI de novo — por isso
 * fica só na Franquia, e some quando a PI está paga.
 *
 * Paga a PI, a baixa aparece embaixo: o que o Asaas disse do pagamento —
 * líquido, compensação, Pix ou cartão — e o PDF do comprovante, que a casa
 * baixa do Asaas e guarda.
 */
function PaymentSection({
  peripheral: p,
  canWork,
  regenerate,
}: {
  peripheral: Peripheral;
  canWork: boolean;
  regenerate: ReturnType<typeof usePeripheralMutations>['regeneratePaymentLink'];
}) {
  const pay = p.payment;
  const charge = pay?.charge ?? null;
  const canRegenerate = canWork && p.stage === 'franquia' && pay?.state !== 'pago';

  return (
    <DetailSection
      icon="dinheiro"
      title="O pagamento da PI"
      actions={
        <>
          {pay && (
            <Badge tone={PAYMENT_TONES[pay.state]}>
              {PAYMENT_LINK_STATE_LABELS[pay.state]}
            </Badge>
          )}
          {canRegenerate && (
            <Button
              size="sm"
              variant="contorno"
              loading={regenerate.isPending}
              onClick={() => regenerate.mutate(undefined)}
            >
              {pay ? 'Gerar link novo' : 'Gerar link e enviar'}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {regenerate.isError && <Alert tone="erro">{regenerate.error.message}</Alert>}
        {!pay ? (
          <p className="text-sm text-texto-suave">
            Nenhum link saiu ainda. Ele vai na mensagem da PI quando a solicitação é
            aprovada.
          </p>
        ) : (
          <Facts>
            <Fact label={<Labeled icon="dinheiro">PI pedida</Labeled>}>
              {money(pay.amount)}
            </Fact>
            <Fact
              label={
                <Labeled icon="relogio">
                  {pay.state === 'aberto' ? 'Link vale até' : 'Link valia até'}
                </Labeled>
              }
            >
              {formatInstant(pay.expiresAt)}
            </Fact>
            <Fact
              label={
                <Labeled icon={charge?.method === 'cartao' ? 'cartao' : 'pix'}>
                  Forma escolhida
                </Labeled>
              }
            >
              {charge?.method ? (
                charge.method === 'cartao' ? (
                  `Cartão em ${charge.installments ?? 1}x`
                ) : (
                  'Pix'
                )
              ) : (
                <NotProvided />
              )}
            </Fact>
            <Fact label={<Labeled icon="pessoa">O associado paga</Labeled>}>
              {charge ? (
                <>
                  {money(charge.total)}
                  {charge.fee && charge.fee !== '0.00'
                    ? ` (taxa ${money(charge.fee)})`
                    : ''}
                </>
              ) : (
                <NotProvided />
              )}
            </Fact>
            {pay.url && (
              <Fact wide label={<Labeled icon="clipe">Link</Labeled>}>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-all text-texto-suave">{pay.url}</span>
                  <CopyButton text={pay.url} label="Copiar link" />
                </span>
              </Fact>
            )}
          </Facts>
        )}
        {charge?.settlement && (
          <div className="flex flex-col gap-3 rounded-borda bg-superficie-suave p-3">
            <h4 className="text-sm font-semibold text-texto">Baixa no Asaas</h4>
            <Facts>
              <SettlementFacts
                settlement={charge.settlement}
                openReceipt={() => peripheralReceiptUrl(p.id)}
              />
            </Facts>
          </div>
        )}
      </div>
    </DetailSection>
  );
}

// @valor — as chaves são os estados do pedido (`PAYMENT_LINK_STATES`)
const PAYMENT_TONES = {
  aberto: 'informacao',
  pago: 'sucesso',
  expirado: 'alerta',
  substituido: 'neutro',
} as const;
